import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Fingerprint, Eye, EyeOff } from 'lucide-react';
import './Login.css';
import {
  isBiometricAvailable,
  hasBiometricEnrolled,
  verifyBiometric,
  registerBiometric,
  isMobileDevice,
  clearBiometricData,
  syncBiometricEnrollment
} from './utils/biometric';
import AddToHomeScreen from './components/AddToHomeScreen';
import VersionBadge from './components/VersionBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [newPin, setNewPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Footer settings from admin
  const [footerSettings, setFooterSettings] = useState({
    siteName: 'DineASaurus.com',
    siteUrl: 'https://dineasaurus.com',
    createdBy: 'Bill Casso',
    version: '',
    copyrightYears: '2025',
  });
  const [userIP, setUserIP] = useState('...');
  
  // Biometric states
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [useBiometric, setUseBiometric] = useState(false);
  const [showEnableBiometric, setShowEnableBiometric] = useState(false);
  
  // Fetch footer settings and IP on mount
  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/footer-settings`);
        if (response.ok) {
          const data = await response.json();
          setFooterSettings(data);
        }
      } catch (err) {
        console.log('Could not fetch footer settings, using defaults');
      }
    };
    
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (response.ok) {
          const data = await response.json();
          setUserIP(data.ip);
        }
      } catch (err) {
        setUserIP('Protected');
      }
    };
    
    fetchFooterSettings();
    fetchIP();
  }, []);
  
  // Check biometric availability on mount
  useEffect(() => {
    const isAvailable = isBiometricAvailable();
    setBiometricAvailable(isAvailable);
    console.log('=== BIOMETRIC DEBUG ===');
    console.log('WebAuthn supported:', window.PublicKeyCredential !== undefined);
    console.log('Is mobile device:', isMobileDevice());
    console.log('Biometric available:', isAvailable);
    
    // Check for remembered email
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
      console.log('Pre-filled email from remember me:', rememberedEmail);
    }
  }, []);
  
  // Check if user has biometric enrolled when email changes.
  // Also syncs enrollment state from the backend so users on iPad Safari
  // (ITP-cleared localStorage) or PWAs (separate localStorage bucket) still
  // see the biometric login option after previously enrolling.
  useEffect(() => {
    let cancelled = false;
    if (email && biometricAvailable) {
      // Fast path: use local cache immediately
      const enrolledLocal = hasBiometricEnrolled(email);
      setUseBiometric(enrolledLocal);
      // Then reconcile with the backend
      syncBiometricEnrollment(email).then((res) => {
        if (cancelled) return;
        setUseBiometric(!!res.enrolled);
        console.log('[biometric] backend-synced enrolled =', res.enrolled);
      });
    } else {
      setUseBiometric(false);
    }
    return () => { cancelled = true; };
  }, [email, biometricAvailable]);
  
  const handleBiometricLogin = async () => {
    if (!email) {
      setError('Please enter your email first');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const result = await verifyBiometric(email);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const data = result.data;
      
      console.log('=== BIOMETRIC LOGIN DEBUG ===');
      console.log('Biometric verification data:', data);
      console.log('Email:', data.email);
      console.log('Display Name:', data.display_name);
      console.log('Is Admin:', data.is_admin);
      console.log('Token:', data.token ? 'Present' : 'Missing');
      
      // Store token and user info - always use localStorage for biometric login
      // (if user enrolled biometrics, they want to stay logged in)
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_email', data.email);
      localStorage.setItem('display_name', data.display_name);
      localStorage.setItem('is_admin', String(data.is_admin)); // Convert to string explicitly
      localStorage.setItem('remembered_email', data.email);
      
      console.log('Stored in localStorage:', {
        auth_token: localStorage.getItem('auth_token') ? 'Present' : 'Missing',
        user_email: localStorage.getItem('user_email'),
        display_name: localStorage.getItem('display_name'),
        is_admin: localStorage.getItem('is_admin')
      });
      
      console.log('Calling onLogin with:', data.email, data.token ? 'token present' : 'no token', data.display_name, data.is_admin);
      
      // Call onLogin AND wait for it to complete
      await onLogin(
        String(data.email), 
        String(data.token), 
        String(data.display_name), 
        Boolean(data.is_admin),
        data.groups || '',
        data.owned_group || ''
      );
    } catch (err) {
      console.error('=== BIOMETRIC LOGIN ERROR ===', err);
      setError(err.message || 'Biometric authentication failed');
      alert(`Biometric login failed: ${err.message}\n\nPlease try using your PIN instead.`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnableBiometric = async (loginData) => {
    try {
      const result = await registerBiometric(loginData.email, loginData.token);
      
      if (result.success) {
        setShowEnableBiometric(false);
        // Now complete the login
        onLogin(loginData.email, loginData.token, loginData.displayName, loginData.isAdmin, loginData.groups, loginData.ownedGroup || '');
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Failed to enable biometric:', err);
      alert('Failed to enable fingerprint authentication: ' + err.message);
      // Still complete login even if biometric enrollment fails
      setShowEnableBiometric(false);
      onLogin(loginData.email, loginData.token, loginData.displayName, loginData.isAdmin, loginData.groups, loginData.ownedGroup || '');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password: pin,
          remember_me: rememberMe,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      console.log('Login successful, token received:', data.token.substring(0, 20));
      
      // Store token, email, display_name, is_admin, and groups
      if (rememberMe) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_email', data.email);
        localStorage.setItem('display_name', data.display_name);
        localStorage.setItem('is_admin', String(data.is_admin)); // Always store as string
        localStorage.setItem('user_groups', data.groups || '');
        localStorage.setItem('remembered_email', data.email);
        console.log('Stored in localStorage');
      } else {
        sessionStorage.setItem('auth_token', data.token);
        sessionStorage.setItem('user_email', data.email);
        sessionStorage.setItem('display_name', data.display_name);
        sessionStorage.setItem('is_admin', String(data.is_admin)); // Always store as string
        sessionStorage.setItem('user_groups', data.groups || '');
        localStorage.removeItem('remembered_email');
        console.log('Stored in sessionStorage');
      }

      console.log('Calling onLogin callback...');
      
      // Check if we should offer biometric enrollment (mobile + not enrolled yet)
      if (biometricAvailable && !hasBiometricEnrolled(email)) {
        console.log('✅ Should show biometric enrollment dialog');
        setShowEnableBiometric(true);
        // Store login data temporarily for after biometric enrollment
        window._pendingLogin = {
          email: data.email,
          token: data.token,
          displayName: data.display_name,
          isAdmin: data.is_admin,
          groups: data.groups || '',
          ownedGroup: data.owned_group || ''
        };
        // Don't call onLogin yet - wait for biometric dialog to be handled
      } else {
        console.log('❌ Not showing biometric enrollment:', {
          biometricAvailable,
          hasEnrolled: hasBiometricEnrolled(email)
        });
        // No biometric enrollment needed, proceed with login
        onLogin(data.email, data.token, data.display_name, data.is_admin, data.groups || '', data.owned_group || '');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (pin.length < 4 || pin.length > 12) {
      setError('Password or PIN must be 4-12 characters');
      setLoading(false);
      return;
    }

    if (!displayName.trim()) {
      setError('Display name is required');
      setLoading(false);
      return;
    }

    // Validate email format for registration
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address for registration');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          display_name: displayName,
          pin,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Store token, email, display_name, and is_admin
      if (rememberMe) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_email', data.email);
        localStorage.setItem('display_name', data.display_name);
        localStorage.setItem('is_admin', String(data.is_admin)); // Always store as string
        localStorage.setItem('remembered_email', data.email);
      } else {
        sessionStorage.setItem('auth_token', data.token);
        sessionStorage.setItem('user_email', data.email);
        sessionStorage.setItem('display_name', data.display_name);
        sessionStorage.setItem('is_admin', String(data.is_admin)); // Always store as string
        localStorage.removeItem('remembered_email');
      }

      onLogin(data.email, data.token, data.display_name, data.is_admin, data.groups || '', data.owned_group || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (newPin.length < 4 || newPin.length > 12) {
      setError('Password or PIN must be 4-12 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-pin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          new_pin: newPin,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'PIN reset failed');
      }

      alert('PIN reset successfully!\n\nYou can now login with your new PIN.\n\nNote: You\'ll need to re-register biometric authentication on your next login.');
      
      // Clear stored biometric credentials since PIN has changed
      clearBiometricData(email);
      
      setShowForgotPin(false);
      setNewPin('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <AddToHomeScreen />
      <div className="login-content">
        <div className="login-box">
        <div className="login-header">
          <h1 className="login-title">Dine-A-Saurus</h1>
          <div className="login-logo-line-two">
            <span className="login-logo-dino">🦖</span>
            <p className="login-tagline">Tracking your dining adventures!</p>
          </div>
          <div className="login-footprints">
            <span className="dino-track">
              <span className="toe toe-left"></span>
              <span className="toe toe-center"></span>
              <span className="toe toe-right"></span>
            </span>
            <span className="dino-track track-offset">
              <span className="toe toe-left"></span>
              <span className="toe toe-center"></span>
              <span className="toe toe-right"></span>
            </span>
            <span className="dino-track">
              <span className="toe toe-left"></span>
              <span className="toe toe-center"></span>
              <span className="toe toe-right"></span>
            </span>
            <span className="dino-track track-offset">
              <span className="toe toe-left"></span>
              <span className="toe toe-center"></span>
              <span className="toe toe-right"></span>
            </span>
          </div>
        </div>

        {!showForgotPin && !showRegister ? (
          <form onSubmit={handleLogin} className="login-form" autoComplete="off" data-lpignore="true" data-form-type="other">
            <div className="form-field">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                name="email-address"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                data-lpignore="true"
              />
            </div>

            {/* Warning when biometric is available but not enrolled in this browser */}
            {biometricAvailable && email && !useBiometric && (
              <div style={{
                marginTop: '10px',
                padding: '10px 12px',
                background: '#FEF3C7',
                color: '#92400E',
                borderRadius: '6px',
                fontSize: '0.8rem',
                border: '1px solid #FCD34D',
                lineHeight: '1.4'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>⚠️ Fingerprint Not Set Up in This Browser</div>
                <div>Login with your PIN first, then enable fingerprint for this browser. Or switch to your usual browser where fingerprint is already set up.</div>
              </div>
            )}

            <div className="form-field">
              <Label htmlFor="pin">Password or PIN (4-12 characters)</Label>
              <div className="pin-field-wrap">
<Input
                id="pin"
                type={showPin ? 'text' : 'password'}
                name="user-pin"
                placeholder="Enter 4-12 characters"
                value={pin}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 12);
                  setPin(value);
                }}
                required
                minLength={4}
                maxLength={12}
                autoComplete="off"
                data-lpignore="true"
              />
              <button
                type="button"
                className="pin-eye-btn"
                onClick={() => setShowPin((v) => !v)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                title={showPin ? 'Hide PIN' : 'Show PIN'}
                data-testid="login-pin-toggle-visibility"
              >
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              </div>
            </div>

            <div className="remember-me-field" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <Label htmlFor="remember" style={{ marginLeft: '8px', cursor: 'pointer' }}>
                  Remember me
                </Label>
              </div>
              <button
                type="button"
                className="forgot-pin-link"
                onClick={() => setShowForgotPin(true)}
              >
                Forgot PIN?
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Show biometric login if available and enrolled */}
            {biometricAvailable && useBiometric && (
              <Button
                type="button"
                onClick={handleBiometricLogin}
                className="biometric-button"
                disabled={loading || !email}
                style={{
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  backgroundColor: '#10b981',
                  color: 'white'
                }}
              >
                <Fingerprint size={20} />
                {loading ? 'Authenticating...' : 'Login with Fingerprint'}
              </Button>
            )}

            <Button
              type="submit"
              className="login-button"
              disabled={loading || pin.length < 4 || pin.length > 12}
              style={biometricAvailable && useBiometric ? { marginBottom: '5px' } : {}}
            >
              {loading ? 'Logging in...' : (biometricAvailable && useBiometric ? 'Use PIN Instead' : 'Login')}
            </Button>
          </form>
        ) : showRegister ? (
          <form onSubmit={handleRegister} className="login-form">
            <p className="forgot-pin-text">
              Create your Dine-A-Saurus account
            </p>

            <div className="form-field">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-field">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Your Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <Label htmlFor="pin">Password or PIN (4-12 characters)</Label>
              <div className="pin-field-wrap">
<Input
                id="pin"
                type={showPin ? 'text' : 'password'}
                placeholder="Enter 4-12 characters"
                value={pin}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 12);
                  setPin(value);
                }}
                required
                minLength={4}
                maxLength={12}
              />
              <button
                type="button"
                className="pin-eye-btn"
                onClick={() => setShowPin((v) => !v)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                title={showPin ? 'Hide PIN' : 'Show PIN'}
                data-testid="register-pin-toggle-visibility"
              >
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              </div>
            </div>

            <div className="remember-me-field">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <Label htmlFor="remember" style={{ marginLeft: '8px', cursor: 'pointer' }}>
                Remember me
              </Label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <Button
              type="submit"
              className="login-button"
              disabled={loading || pin.length < 4 || pin.length > 12}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>

            <button
              type="button"
              className="forgot-pin-link"
              onClick={() => {
                setShowRegister(false);
                setDisplayName('');
                setError('');
              }}
            >
              Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotPin} className="login-form">
            <p className="forgot-pin-text">
              Enter your email and a new 4-digit PIN
            </p>

            <div className="form-field">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <Label htmlFor="newPin">New Password or PIN (4-12 characters)</Label>
              <div className="pin-field-wrap">
<Input
                id="newPin"
                type={showPin ? 'text' : 'password'}
                placeholder="Enter 4-12 characters"
                value={newPin}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 12);
                  setNewPin(value);
                }}
                required
                minLength={4}
                maxLength={12}
              />
              <button
                type="button"
                className="pin-eye-btn"
                onClick={() => setShowPin((v) => !v)}
                aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                title={showPin ? 'Hide PIN' : 'Show PIN'}
                data-testid="forgot-new-pin-toggle-visibility"
              >
                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <Button
              type="submit"
              className="login-button"
              disabled={loading || newPin.length < 4 || newPin.length > 12}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <button
              type="button"
              className="forgot-pin-link"
              onClick={() => {
                setShowForgotPin(false);
                setNewPin('');
                setError('');
              }}
            >
              Back to Login
            </button>
          </form>
        )}
        
        {/* Request Access & Learn More Links */}
        <div style={{
          marginTop: '1.25rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            Not signed up?{' '}
            <Link
              to="/request-access"
              data-testid="request-access-link"
              style={{
                color: '#3b82f6',
                fontWeight: '600',
                textDecoration: 'underline',
              }}
            >
              Request access here
            </Link>
          </div>
          <Link
            to="/learn-more"
            data-testid="learn-more-link"
            style={{
              color: '#6366f1',
              fontSize: '0.9rem',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#4f46e5'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6366f1'}
          >
            Want to Learn More?
          </Link>
        </div>

        {/* Footer inside login box */}
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <a 
              href={footerSettings.siteUrl} 
              style={{ 
                color: '#2c5f2d', 
                textDecoration: 'none', 
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
              data-testid="footer-site-name"
            >
              © {footerSettings.copyrightYears || '2025'} {footerSettings.siteName || 'DineASaurus.com'}
              <VersionBadge version={footerSettings.version} />
            </a>
          </div>
          <div style={{ color: '#64748b', fontSize: '0.8rem' }} data-testid="footer-created-by">
            Created by {footerSettings.createdBy}
          </div>
          <div style={{ 
            marginTop: '0.75rem', 
            paddingTop: '0.5rem',
            borderTop: '1px solid #f1f5f9',
            color: '#94a3b8', 
            fontSize: '0.7rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <span>🔒</span>
            <span>Secure Connection</span>
            <span>•</span>
            <span>IP: {userIP}</span>
          </div>
        </div>
        </div>
      </div>

      {/* Enable Biometric Dialog */}
      {showEnableBiometric && (
        <div 
          className="biometric-dialog-overlay" 
          onClick={(e) => {
            e.stopPropagation();
            // Don't close on overlay click, only on button click
          }}
          style={{ position: 'fixed', inset: 0 }}
        >
          <div className="biometric-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="biometric-dialog-header">
              <Fingerprint size={48} color="#10b981" />
              <h2>Enable Fingerprint Login?</h2>
            </div>
            <p className="biometric-dialog-text">
              Login faster on your mobile device using your fingerprint. You can always use your PIN if needed.
            </p>
            <div className="biometric-dialog-actions">
              <Button
                onClick={() => {
                  if (window._pendingLogin) {
                    handleEnableBiometric(window._pendingLogin);
                    delete window._pendingLogin;
                  }
                }}
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                Enable Fingerprint
              </Button>
              <Button
                onClick={() => {
                  setShowEnableBiometric(false);
                  // Complete login without biometric
                  if (window._pendingLogin) {
                    onLogin(
                      window._pendingLogin.email,
                      window._pendingLogin.token,
                      window._pendingLogin.displayName,
                      window._pendingLogin.isAdmin,
                      window._pendingLogin.groups,
                      window._pendingLogin.ownedGroup || ''
                    );
                    delete window._pendingLogin;
                  }
                }}
                variant="outline"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

 
    </div>
  );
}

export default Login;
